<template>
  <div>
    <b-card title="Modifier une voiture">
      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          Chargement des données...
        </p>
      </div>

      <validation-observer
        v-else
        ref="simpleRules"
      >
        <b-form @submit.prevent="submitForm">
          <!-- Tabs for organizing form fields -->
          <b-tabs content-class="mt-3">
            <!-- Basic Information Tab -->
            <b-tab
              title="Informations de base"
              active
            >
              <b-row>
                <!-- Basic information -->
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Matricule"
                    label-for="serial_number"
                  >
                    <validation-provider
                      #default="{ errors: serialNumberErrors }"
                      name="Matricule"
                      rules="required"
                    >
                      <b-form-input
                        id="serial_number"
                        v-model="form.serial_number"
                        :state="serialNumberErrors.length > 0 ? false : null"
                        placeholder="Matricule"
                        required
                      />
                      <small class="text-danger">{{ serialNumberErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Marque"
                    label-for="brand"
                  >
                    <validation-provider
                      #default="{ errors: brandErrors }"
                      name="Marque"
                      rules="required"
                    >
                      <b-form-input
                        id="brand"
                        v-model="form.brand"
                        :state="brandErrors.length > 0 ? false : null"
                        placeholder="Marque"
                        required
                      />
                      <small class="text-danger">{{ brandErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Modèle"
                    label-for="model"
                  >
                    <b-form-input
                      id="model"
                      v-model="form.model"
                      placeholder="Modèle"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Année"
                    label-for="year"
                  >
                    <b-form-input
                      id="year"
                      v-model="form.year"
                      type="number"
                      min="1900"
                      max="2100"
                      placeholder="Année"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Couleur"
                    label-for="color"
                  >
                    <validation-provider
                      #default="{ errors: colorErrors }"
                      name="Couleur"
                      rules="required"
                    >
                      <b-form-input
                        id="color"
                        v-model="form.color"
                        :state="colorErrors.length > 0 ? false : null"
                        placeholder="Couleur"
                        required
                      />
                      <small class="text-danger">{{ colorErrors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Numéro de plaque"
                    label-for="plate_number"
                  >
                    <b-form-input
                      id="plate_number"
                      v-model="form.plate_number"
                      placeholder="Numéro de plaque"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Numéro d'identification (VIN)"
                    label-for="vin"
                  >
                    <b-form-input
                      id="vin"
                      v-model="form.vin"
                      placeholder="VIN"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Propriétaire"
                    label-for="owner"
                  >
                    <b-form-input
                      id="owner"
                      v-model="form.owner"
                      placeholder="Propriétaire"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Technical Specifications Tab -->
            <b-tab title="Spécifications techniques">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Capacité (m³)"
                    label-for="capacity"
                  >
                    <b-form-input
                      id="capacity"
                      v-model="form.capacity"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Capacité en mètres cubes"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Poids maximal (kg)"
                    label-for="max_weight"
                  >
                    <b-form-input
                      id="max_weight"
                      v-model="form.max_weight"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Poids maximal en kg"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Cylindrée moteur (L)"
                    label-for="engine_size"
                  >
                    <b-form-input
                      id="engine_size"
                      v-model="form.engine_size"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Taille du moteur en litres"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Transmission"
                    label-for="transmission"
                  >
                    <v-select
                      id="transmission"
                      v-model="form.transmission"
                      :options="transmissionOptions"
                      placeholder="Sélectionner type de transmission"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Type de carburant"
                    label-for="fuel_type"
                  >
                    <v-select
                      id="fuel_type"
                      v-model="form.fuel_type"
                      :options="fuelTypeOptions"
                      placeholder="Sélectionner type de carburant"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Nombre de portes"
                    label-for="doors"
                  >
                    <b-form-input
                      id="doors"
                      v-model="form.doors"
                      type="number"
                      min="0"
                      placeholder="Nombre de portes"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Nombre de sièges"
                    label-for="seats"
                  >
                    <b-form-input
                      id="seats"
                      v-model="form.seats"
                      type="number"
                      min="0"
                      placeholder="Nombre de sièges"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Maintenance Info Tab -->
            <b-tab title="Informations d'entretien">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Kilométrage"
                    label-for="mileage"
                  >
                    <b-form-input
                      id="mileage"
                      v-model="form.mileage"
                      type="number"
                      min="0"
                      placeholder="Kilométrage actuel"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'immatriculation"
                    label-for="registration_date"
                  >
                    <b-form-datepicker
                      id="registration_date"
                      v-model="form.registration_date"
                      placeholder="Date d'immatriculation"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Dernière date d'entretien"
                    label-for="last_maintenance_date"
                  >
                    <b-form-datepicker
                      id="last_maintenance_date"
                      v-model="form.last_maintenance_date"
                      placeholder="Dernière date d'entretien"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Prochaine date d'entretien"
                    label-for="next_maintenance_date"
                  >
                    <b-form-datepicker
                      id="next_maintenance_date"
                      v-model="form.next_maintenance_date"
                      placeholder="Prochaine date d'entretien"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'expiration de l'assurance"
                    label-for="insurance_expiry"
                  >
                    <b-form-datepicker
                      id="insurance_expiry"
                      v-model="form.insurance_expiry"
                      placeholder="Date d'expiration de l'assurance"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Date d'expiration du contrôle technique"
                    label-for="technical_inspection_expiry"
                  >
                    <b-form-datepicker
                      id="technical_inspection_expiry"
                      v-model="form.technical_inspection_expiry"
                      placeholder="Date d'expiration du contrôle technique"
                      locale="fr"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <!-- Assignment Tab -->
            <b-tab title="Affectation">
              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Conducteur"
                    label-for="default_driver"
                  >
                    <v-select
                      id="default_driver"
                      v-model="form.default_driver"
                      :options="drivers"
                      label="name"
                      :reduce="driver => driver.id"
                      placeholder="Sélectionner un conducteur"
                      :clearable="true"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label="Statut"
                    label-for="status"
                  >
                    <v-select
                      id="status"
                      v-model="form.status"
                      :options="statusOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Statut de la voiture"
                      class="select-search-option"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Rental/Location Information -->
              <b-row class="mt-3">
                <b-col md="12">
                  <div class="rental-section-header">
                    <feather-icon
                      icon="KeyIcon"
                      size="20"
                      class="text-primary mr-2"
                    />
                    <h5 class="mb-0">
                      Informations de location
                    </h5>
                  </div>
                  <hr class="mt-2 mb-3">
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group>
                    <b-form-checkbox
                      id="is_rented"
                      v-model="form.is_rented"
                      name="is_rented"
                      switch
                    >
                      <span class="font-small-3">
                        {{ form.is_rented ? 'Voiture en location' : 'Voiture non louée' }}
                      </span>
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

                <b-col
                  md="6"
                  sm="12"
                >
                  <b-form-group
                    label-for="monthly_rental_amount"
                  >
                    <template #label>
                      <div class="d-flex align-items-center">
                        <feather-icon
                          icon="DollarSignIcon"
                          size="16"
                          class="text-success mr-1"
                        />
                        <span>Montant de location par mois</span>
                      </div>
                    </template>
                    <b-form-input
                      id="monthly_rental_amount"
                      v-model="form.monthly_rental_amount"
                      type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                      min="0"
                      placeholder="Montant mensuel de location"
                      :disabled="!form.is_rented"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  md="12"
                  sm="12"
                >
                  <b-form-group
                    label="Description"
                    label-for="description"
                  >
                    <b-form-textarea
                      id="description"
                      v-model="form.description"
                      rows="3"
                      placeholder="Description de la voiture"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col
                  md="12"
                  sm="12"
                >
                  <b-form-group>
                    <b-form-checkbox
                      id="is_active"
                      v-model="form.is_active"
                      name="is_active"
                      switch
                      size="lg"
                    >
                      {{ form.is_active ? 'Actif' : 'Inactif' }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>

          <b-row class="mt-2">
            <b-col>
              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !isFormValid"
                class="mr-2"
                aria-label="Enregistrer les modifications du véhicule"
              >
                <span v-if="isSubmitting">
                  <b-spinner small />
                  Sauvegarde...
                </span>
                <span v-else>Enregistrer</span>
              </b-button>
              <b-button
                variant="outline-secondary"
                :to="{ name: 'car-list' }"
                aria-label="Annuler et retourner à la liste des véhicules"
              >
                Annuler
              </b-button>
            </b-col>
          </b-row>
          <div
            id="car-edit-help"
            class="sr-only"
          >
            Modifiez les informations du véhicule sélectionné. Les champs obligatoires sont signalés. Les modifications sont enregistrées après validation.
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BFormCheckbox,
  BRow,
  BCol,
  BSpinner,
  BTabs,
  BTab,
  BFormDatepicker,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BFormCheckbox,
    BRow,
    BCol,
    BSpinner,
    BTabs,
    BTab,
    BFormDatepicker,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      form: {
        serial_number: '',
        brand: '',
        model: '',
        year: null,
        color: '',
        plate_number: '',
        vin: '',
        engine_size: null,
        transmission: null,
        fuel_type: null,
        doors: null,
        seats: null,
        capacity: null,
        max_weight: null,
        mileage: null,
        registration_date: null,
        last_maintenance_date: null,
        next_maintenance_date: null,
        insurance_expiry: null,
        technical_inspection_expiry: null,
        owner: '',
        default_driver: null,
        description: '',
        is_rented: false,
        monthly_rental_amount: null,
        status: 'available',
        is_active: true,
      },
      drivers: [],
      isLoading: true,
      isSubmitting: false,
      errors: {},
      transmissionOptions: [
        { value: 'Automatic', label: 'Automatique' },
        { value: 'Manual', label: 'Manuelle' },
        { value: 'Semi-automatic', label: 'Semi-automatique' },
        { value: 'CVT', label: 'CVT' },
      ],
      fuelTypeOptions: [
        { value: 'Gasoline', label: 'Essence' },
        { value: 'Diesel', label: 'Diesel' },
        { value: 'Electric', label: 'Électrique' },
        { value: 'Hybrid', label: 'Hybride' },
        { value: 'CNG', label: 'Gaz naturel' },
        { value: 'LPG', label: 'GPL' },
      ],
      statusOptions: [
        { value: 'available', label: 'Disponible' },
        { value: 'in_use', label: 'En utilisation' },
        { value: 'maintenance', label: 'En maintenance' },
        { value: 'out_of_service', label: 'Hors service' },
      ],
    }
  },
  computed: {
    isFormValid() {
      return (
        this.form.serial_number.trim() !== ''
        && this.form.brand.trim() !== ''
        && this.form.color.trim() !== ''
      )
    },
  },
  watch: {
    'form.is_rented': function handleIsRentedChange(newValue) {
      if (!newValue) {
        this.form.monthly_rental_amount = null
      }
    },
  },
  created() {
    (async () => {
      try {
        await Promise.all([
          this.loadDrivers(),
          this.fetchCarData(),
        ])
      } finally {
        this.isLoading = false
      }
    })()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async loadDrivers() {
      try {
        const { data } = await axios.get('/api/auth/drivers/', {
          params: { is_active: true },
        })

        this.drivers = data.map(driver => ({
          id: driver.id,
          name: driver.username,
          car: driver.car,
        }))
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des conducteurs')
      }
    },

    async fetchCarData() {
      try {
        const { data } = await axios.get(`/api/cars/detail/${this.$route.params.id}/`)

        // Format data for form
        this.form = {
          serial_number: data.serial_number,
          brand: data.brand,
          model: data.model || '',
          year: data.year,
          color: data.color,
          plate_number: data.plate_number || '',
          vin: data.vin || '',
          engine_size: data.engine_size,
          transmission: data.transmission,
          fuel_type: data.fuel_type,
          doors: data.doors,
          seats: data.seats,
          capacity: data.capacity,
          max_weight: data.max_weight,
          mileage: data.mileage,
          registration_date: data.registration_date,
          last_maintenance_date: data.last_maintenance_date,
          next_maintenance_date: data.next_maintenance_date,
          insurance_expiry: data.insurance_expiry,
          technical_inspection_expiry: data.technical_inspection_expiry,
          owner: data.owner || '',
          default_driver: data.default_driver,
          description: data.description || '',
          is_rented: data.is_rented || false,
          monthly_rental_amount: data.monthly_rental_amount,
          status: data.status || 'available',
          is_active: data.is_active,
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des données de la voiture')
        this.$router.push({ name: 'car-list' })
      }
    },

    async submitForm() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      try {
        this.isSubmitting = true

        // Format data
        const {
          serial_number: serialNumber, brand, model, year, color, plate_number: plateNumber, vin, engine_size: engineSize,
          transmission, fuel_type: fuelType, doors, seats, capacity, max_weight: maxWeight, mileage,
          registration_date: registrationDate, last_maintenance_date: lastMaintenanceDate, next_maintenance_date: nextMaintenanceDate,
          insurance_expiry: insuranceExpiry, technical_inspection_expiry: technicalInspectionExpiry, owner, default_driver: defaultDriver,
          description, is_rented: isRented, monthly_rental_amount: monthlyRentalAmount, status, is_active: isActive,
        } = this.form

        const payload = {
          serial_number: serialNumber,
          brand,
          model,
          year: year ? parseInt(year, 10) : null,
          color,
          plate_number: plateNumber,
          vin,
          engine_size: engineSize ? parseFloat(engineSize) : null,
          transmission,
          fuel_type: fuelType,
          doors: doors ? parseInt(doors, 10) : null,
          seats: seats ? parseInt(seats, 10) : null,
          capacity: capacity ? parseFloat(capacity) : null,
          max_weight: maxWeight ? parseFloat(maxWeight) : null,
          mileage: mileage ? parseInt(mileage, 10) : null,
          registration_date: registrationDate,
          last_maintenance_date: lastMaintenanceDate,
          next_maintenance_date: nextMaintenanceDate,
          insurance_expiry: insuranceExpiry,
          technical_inspection_expiry: technicalInspectionExpiry,
          owner,
          default_driver: defaultDriver,
          description,
          is_rented: isRented,
          monthly_rental_amount: monthlyRentalAmount ? parseFloat(monthlyRentalAmount) : null,
          status,
          is_active: isActive,
        }

        await axios.put(`/api/cars/update/${this.$route.params.id}/`, payload)

        this.showToast('success', 'top-center', 'Voiture modifiée avec succès')
        this.$router.push({ name: 'car-list' })
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data
        }
        this.showToast('danger', 'top-center', 'Erreur lors de la modification de la voiture')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Consistent form field sizing
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #B50100;
    box-shadow: none;
  }
}

// Textarea exception for height
textarea.form-control {
  height: auto;
}

// Improve v-select styling to match regular inputs
.select-search-option {
  .vs__dropdown-toggle {
    padding: 5px 0;
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  .vs__selected-options {
    padding: 0 6px;
    align-items: center;
  }

  .vs__search {
    padding: 5px 8px;
    margin: 0;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  .vs__clear {
    height: 22px;
  }

  .vs__open-indicator {
    height: 17px;
  }
}

// Add styles for the tabs
.nav-tabs {
  border-bottom: 1px solid #ddd;
  margin-bottom: 1rem;

  .nav-link {
    color: #6e6b7b;
    font-weight: 500;

    &.active {
      color: #B50100;
      border-bottom: 2px solid #B50100;
      border-color: transparent transparent #B50100;
    }

    &:hover:not(.active) {
      border-color: transparent;
    }
  }
}

// Simple Rental Section Header
.rental-section-header {
  display: flex;
  align-items: center;
}

</style>
